import React, { Fragment, memo, useEffect } from "react";
import { Router, Route, Switch } from "react-router-dom";
import { createBrowserHistory } from "history";
import './../../../packages/utils/i18n';
import { useTranslation } from "react-i18next";
import { appInitialized } from "src/custom-events";
import api, { useAsync } from "src/api";
import Storage from "src/storage";
import { useEventCb, genUID, applyAlias } from "src/utils";
import Content from "src/content";
import { Load } from "src/component-loader";
import { PortalError, PortalLoading } from "src/primitives";
import { GPTProvider } from "src/gpt";
import AdBlockDetector from "src/gpt/AdBlockDetector";
import LocalizationProvider from "src/localization";

import AddToHomeScreen from "./AddToHomeScreen";
import Sitemap from "../../components/Sitemap";
import PromptGDPR from "./PromptGDPR";
import styles from "./style.css";

import store from "src/store";

//const cachedChildren = Storage.get("cached-children");

const history = createBrowserHistory();
window._history = history;

appInitialized();
const k = GLOBALS.ENV;
const BuildInfo = () => (k !== "production" ? <aside class={styles.buildInfo}>{window.BUILD}</aside> : null);

const App = memo(() => {
  const { clientid: clientId, variant, brand: affiliateId, page, isContentPortal, language } = store("config");
  const { visitorId: userID } = window;

  const useContent = useAsync(api.getContent);
  const { i18n } = useTranslation();
  const pageValue = (isContentPortal && !window.location.href.includes("_search")) ? "content" : page;
  const content = useContent({
    clientId,
    variant,
    affiliateId,
    userID,
    ...(window.location.href.includes("page=") || (isContentPortal && !window.location.href.includes("_search")) ? { page: pageValue } : {}),
    referer:encodeURIComponent(window.location.href)
  });

  useEffect(() => {
    i18n.changeLanguage(language);
  }, []);

  const handleVisibilityChange = () => {
    if (
      !document.hidden &&
      !window.location.pathname.includes("search") &&
      !window.location.pathname.includes("content") &&
      (!config || (config && config.isContentPortal && window.location.pathname !== "/"))       
    ) {
      store.set("config.pageViewId", genUID());
      content.call({ clientId, variant, affiliateId, userID });
    }
  };

  useEventCb("visibilitychange", handleVisibilityChange);

  const isLoading = content.loading || !Object.keys(content.data).length; // loading 'true' if content API is being called, or hasn't been called

  const isContentAI = history.location.pathname && history.location.pathname.length > 0 && history.location.pathname.slice(1,9) === '_content';

  if (isLoading /* && !cachedChildren */) {
    if (isContentAI) return null;
    return <PortalLoading/>;
  } else if (content.error) {
    return <PortalError />;
  }

  const { children, config } = content.data;
  if (window.host && children) applyAlias(children, window.host);
 
  children && store.set("children", children || []);
  config &&
    store.set("config", {
      ...(store("config") || {}),
      ...(config || {}),
      units: (config.country || "").includes("US") ? "imperial" : "metric"
    });
  store.set("search", {
    ...(store("search") || {})
  });
  window.visitorId = config.visitorId || window.visitorId; // TODO: Make reactive (add to base API client)

  if (config.csImpressionPixels) {
    config.csImpressionPixels.forEach(t => {
      if (t.url) {
        const useImpression = useAsync(api.get);
        useImpression(t.url, {}, { mode: "no-cors", credentials: "include" });
      }
    });
  }

  // Set timeout for stringifying to prevent blocking rendering
  setTimeout(() => {
    //children && Storage.set("cached-children", children);
    Storage.set("cached-store", { config: store("config"), state: store("state"), children: store("children") });
  }, 1000);

  const serpLayoutRaw = (children.find(c => c.name == 'serpLayout'))?.props?.serpLayout
  const serpLayout = serpLayoutRaw && JSON.parse(serpLayoutRaw)
  const isContentSerp = Boolean(serpLayout && serpLayout.some(serp => serp.name == "ArticlePage" && serp.enabled && serp.active))

  return (
    <div class={styles.container}>
      <GPTProvider isContentSerp={isContentSerp}>
        <LocalizationProvider labels={config.i18nLabels}>
          <AdBlockDetector />
          <PromptGDPR />
          <Router history={history}>
            <Fragment>
            <Route path="*" render={({ location }) => (location.pathname.indexOf("/_search") < 0 && !location.pathname.includes("sitemap") && <Content content={children /*|| cachedChildren*/}/>)} />
            <Switch>
                <Route path="/_apps" render={props => <Load {...props} name="Apps" />} />
                <Route path="/_local" render={props => <Load {...props} name="Local" />} />
                <Route path="/_social" render={props => <Load {...props} name="Social" modal />} />
                <Route path="/_horoscope" render={props => <Load {...props} name="Horoscope" modal />} />
                <Route path="/_articles/:postId/:insideNews?" render={props => <Load {...props} name="OriginalContent" modal />} />
                <Route path="/(_search|_gcse)" render={props => <Load {...props} name="Search" />} />
                <Route path="/_content" render={props => <Load {...props} name="PortalContentAI" />} />
                <Route path="/sitemap/:page/:category?" render={props => <Sitemap {...props} />} />
                {config.isContentPortal && <Route path="/" render={props => <Load {...props} name="PortalContentAI" />} />}
              </Switch>
            </Fragment>
          </Router>
        </LocalizationProvider>
        <AddToHomeScreen />
        <BuildInfo />
      </GPTProvider>
    </div>
  );
});

export default App;
